import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, Copy, FileText, CheckCircle, Clock, Eye, Send } from 'lucide-react'
import { getEnvelope, type EnvelopeWithRecipients } from '../services/envelopeService'
import { downloadPdf, createPdfBlobUrl } from '@/shared/lib/pdf-utils'
import toast from 'react-hot-toast'

export function EnvelopeDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [envelope, setEnvelope] = useState<EnvelopeWithRecipients | null>(null)
  const [loading, setLoading] = useState(true)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    loadEnvelope(id)
  }, [id])

  async function loadEnvelope(envelopeId: string) {
    try {
      setLoading(true)
      const data = await getEnvelope(envelopeId)
      setEnvelope(data)
      if (data?.document_data) {
        const url = createPdfBlobUrl(data.document_data)
        setPdfUrl(url)
      }
    } catch (err) {
      console.error('Error loading envelope:', err)
      toast.error('Error al cargar el documento')
    } finally {
      setLoading(false)
    }
  }

  const handleDownload = () => {
    if (!envelope) return
    const filename = envelope.status === 'completed'
      ? envelope.original_filename.replace(/\.pdf$/i, '_firmado.pdf')
      : envelope.original_filename
    downloadPdf(envelope.document_data, filename)
  }

  const copySigningLink = (token: string) => {
    const link = `${window.location.origin}/firmar-invitacion/${token}`
    navigator.clipboard.writeText(link)
    toast.success('Link copiado')
  }

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12 text-legal-500 dark:text-legal-400">
        Cargando documento...
      </div>
    )
  }

  if (!envelope) {
    return (
      <div className="max-w-4xl mx-auto text-center py-12">
        <p className="text-legal-500 dark:text-legal-400 mb-4">Documento no encontrado</p>
        <button
          onClick={() => navigate('/documentos')}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm"
        >
          Volver a Documentos
        </button>
      </div>
    )
  }

  const recipients = envelope.sgw_envelope_recipients || []

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/documentos')}
        className="flex items-center gap-2 text-sm text-legal-500 dark:text-legal-400 hover:text-legal-700 dark:hover:text-legal-200 mb-4"
      >
        <ArrowLeft size={16} />
        Volver a Documentos
      </button>

      {/* Header */}
      <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-legal-900 dark:text-white">
              {envelope.title}
            </h1>
            <p className="text-sm text-legal-500 dark:text-legal-400 mt-1">
              {envelope.original_filename}
            </p>
            <p className="text-xs text-legal-400 dark:text-legal-500 mt-1">
              Creado el {new Date(envelope.created_at).toLocaleDateString('es-AR', {
                year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-xs font-medium ${
              envelope.status === 'completed'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                : envelope.status === 'sent'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                : 'bg-legal-100 dark:bg-legal-700 text-legal-600 dark:text-legal-300'
            }`}>
              {envelope.status === 'completed' ? 'Completado' :
               envelope.status === 'sent' ? 'Esperando firma' :
               envelope.status === 'voided' ? 'Anulado' : 'Borrador'}
            </span>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
            >
              <Download size={16} />
              Descargar
            </button>
          </div>
        </div>

        {envelope.message && (
          <p className="text-sm text-legal-600 dark:text-legal-300 mt-4 p-3 bg-legal-50 dark:bg-legal-900 rounded-lg">
            {envelope.message}
          </p>
        )}
      </div>

      {/* Recipients */}
      {recipients.length > 0 && (
        <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-6 mb-6">
          <h2 className="text-sm font-semibold text-legal-700 dark:text-legal-200 mb-4">
            Firmantes
          </h2>
          <div className="space-y-3">
            {recipients.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between p-3 bg-legal-50 dark:bg-legal-900 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {r.status === 'signed' ? (
                    <CheckCircle size={20} className="text-green-500" />
                  ) : r.status === 'viewed' ? (
                    <Eye size={20} className="text-blue-500" />
                  ) : r.status === 'sent' ? (
                    <Send size={20} className="text-legal-400" />
                  ) : (
                    <Clock size={20} className="text-legal-400" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-legal-800 dark:text-legal-100">
                      {r.name}
                    </p>
                    <p className="text-xs text-legal-500 dark:text-legal-400">{r.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    r.status === 'signed'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300'
                      : r.status === 'viewed'
                      ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300'
                      : 'bg-legal-100 dark:bg-legal-700 text-legal-500 dark:text-legal-400'
                  }`}>
                    {r.status === 'signed' ? `Firmado ${r.signed_at ? new Date(r.signed_at).toLocaleDateString('es-AR') : ''}` :
                     r.status === 'viewed' ? 'Visto' :
                     r.status === 'sent' ? 'Enviado' : 'Pendiente'}
                  </span>
                  {r.status !== 'signed' && (
                    <button
                      onClick={() => copySigningLink(r.access_token)}
                      className="p-1.5 text-legal-400 hover:text-primary-600 dark:hover:text-primary-400 rounded"
                      title="Copiar link de firma"
                    >
                      <Copy size={14} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PDF Preview */}
      {pdfUrl && (
        <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-legal-200 dark:border-legal-700">
            <p className="text-sm font-medium text-legal-700 dark:text-legal-200 flex items-center gap-2">
              <FileText size={16} />
              Vista previa
              {envelope.status === 'completed' && (
                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-300 px-2 py-0.5 rounded-full">
                  Documento final con todas las firmas
                </span>
              )}
            </p>
          </div>
          <iframe
            src={pdfUrl}
            className="w-full h-[500px] border-0"
            title="Vista previa del documento"
          />
        </div>
      )}
    </div>
  )
}
